/**
 * GhostForm — Shared ghost-style form components
 * Updated for the "1 Million Euro Design" (Glassmorphism, Focus mode)
 */
import React, { useState } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { motion } from "framer-motion";

export const SectionCard = ({ id, title, subtitle, children }) => (
  <Box
    id={id}
    sx={{
      mb: 5,
      p: { xs: 3, md: 4 },
      borderRadius: "20px",
      border: "1px solid rgba(255, 255, 255, 0.05)",
      background: "rgba(20, 20, 20, 0.4)",
      backdropFilter: "blur(20px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
    }}
  >
    <Typography
      sx={{
        color: "#fff",
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 700,
        fontSize: "1.2rem",
        mb: 0.5,
        letterSpacing: "-0.01em"
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        sx={{
          color: "rgba(255, 255, 255, 0.45)",
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.85rem",
          mb: 4,
        }}
      >
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

export const GhostInput = ({
  label,
  value,
  onChange,
  multiline,
  rows,
  placeholder,
  error,
  helperText,
  type,
  sx: outerSx,
  ...props
}) => {
  const [focused, setFocused] = useState(false);

  return (
    <Box sx={{ mb: 3, width: "100%", ...outerSx }}>
      {label && (
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.45)",
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.75rem",
            fontWeight: 600,
            mb: 1,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </Typography>
      )}
      <motion.div
        animate={{
          borderColor: focused ? "rgba(139, 92, 246, 0.6)" : "rgba(255, 255, 255, 0.08)",
          boxShadow: focused ? "0 0 0 2px rgba(139, 92, 246, 0.15)" : "none",
          backgroundColor: focused ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.02)",
        }}
        transition={{ duration: 0.2 }}
        style={{
          borderRadius: "12px",
          border: error ? "1px solid #ef4444" : "1px solid rgba(255, 255, 255, 0.08)",
          padding: multiline ? "12px 16px" : "0px 16px",
          display: "flex",
          alignItems: multiline ? "flex-start" : "center",
          minHeight: multiline ? "auto" : "48px",
        }}
      >
        <InputBase
          type={type || "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          multiline={multiline}
          rows={multiline ? rows || 3 : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{
            color: "#fff",
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.9rem",
            width: "100%",
            "& input": {
              padding: 0,
              height: "100%",
              "&::placeholder": { color: "rgba(255, 255, 255, 0.25)", opacity: 1 },
            },
            "& textarea": {
              padding: 0,
              "&::placeholder": { color: "rgba(255, 255, 255, 0.25)", opacity: 1 },
            },
          }}
          {...props}
        />
      </motion.div>
      {helperText && (
        <Typography
          sx={{
            color: error ? "#ef4444" : "rgba(255, 255, 255, 0.3)",
            fontSize: "0.72rem",
            mt: 0.5,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export const GhostSelect = ({ label, value, onChange, options }) => {
  const [focused, setFocused] = useState(false);

  return (
    <Box sx={{ mb: 3 }}>
      {label && (
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.45)",
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.75rem",
            fontWeight: 600,
            mb: 1,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </Typography>
      )}
      <motion.div
        animate={{
          borderColor: focused ? "rgba(139, 92, 246, 0.6)" : "rgba(255, 255, 255, 0.08)",
          boxShadow: focused ? "0 0 0 2px rgba(139, 92, 246, 0.15)" : "none",
          backgroundColor: focused ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.02)",
        }}
        transition={{ duration: 0.2 }}
        style={{
          borderRadius: "12px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "0px 16px",
          display: "flex",
          alignItems: "center",
          height: "48px",
        }}
      >
        <Box
          component="select"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{
            width: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#fff",
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.9rem",
            height: "100%",
            cursor: "pointer",
            appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='rgba(255,255,255,0.4)' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0px center",
            backgroundSize: "20px",
          }}
        >
          {options.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              style={{ background: "var(--mui-palette-background-default)", color: "#fff" }}
            >
              {opt.label}
            </option>
          ))}
        </Box>
      </motion.div>
    </Box>
  );
};
